import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Spin, message, Empty, Alert } from 'antd';
import { ArrowLeftOutlined, WalletOutlined, DollarOutlined, CopyOutlined, ReloadOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  error: '#EF4444',
  warning: '#F59E0B',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};

  /* Fix Alert text colors */
  .ant-alert {
    color: ${COLORS.textPrimary} !important;
    
    .ant-alert-message {
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-alert-description {
      color: ${COLORS.textSecondary} !important;
      
      p, ul, li {
        color: ${COLORS.textSecondary} !important;
      }
    }
  }

  /* Fix Empty component text */
  .ant-empty {
    .ant-empty-description {
      color: ${COLORS.textSecondary} !important;
    }
  }

  /* Fix pagination text */
  .ant-pagination {
    color: ${COLORS.textPrimary} !important;
    
    .ant-pagination-item {
      a {
        color: ${COLORS.textPrimary} !important;
      }
      
      &.ant-pagination-item-active {
        a {
          color: ${COLORS.accent} !important;
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      .ant-pagination-item-link {
        color: ${COLORS.textPrimary} !important;
      }
    }
    
    .ant-pagination-options {
      color: ${COLORS.textPrimary} !important;
      
      .ant-select-selector {
        color: ${COLORS.textPrimary} !important;
      }
    }
    
    .ant-pagination-total-text {
      color: ${COLORS.textSecondary} !important;
    }
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
`;

const ChainCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 16px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
`;

const BalanceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid ${COLORS.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const BalanceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const BalanceAmounts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const CurrencyIcon = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const ChainIcon = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
`;

const GasStation: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState<any>(null);
  const [gasWallet, setGasWallet] = useState<any>(null);
  const [gasBalances, setGasBalances] = useState<any>(null);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [loadingGasWallet, setLoadingGasWallet] = useState(false);
  const [hasGasWalletId, setHasGasWalletId] = useState<boolean | null>(null);

  const fetchCompanyData = async () => {
    if (!companyId) return;

    try {
      const companyResponse = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_COMPANY_DETAILS.replace('{companyId}', companyId)
      );

      if (companyResponse.data?.status === 'success' && companyResponse.data?.data) {
        const data = companyResponse.data.data;
        setCompanyData(data);
        const hasGasWallet = !!data.gasWalletId;
        setHasGasWalletId(hasGasWallet);
        return hasGasWallet;
      }
    } catch (companyError: any) {
      console.error('Error fetching company details:', companyError);
      message.error('Failed to load company information');
    }
    return false;
  };

  const fetchGasWallet = async () => {
    if (!companyId) return;

    try {
      setLoadingGasWallet(true);
      // Fetch gas wallet details
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_GAS_WALLET.replace('{companyId}', companyId)
      );

      if (response.data?.status === 'success' && response.data?.data) {
        setGasWallet(response.data.data.wallet);
        setGasBalances(response.data.data.gasBalances);
      }
    } catch (error: any) {
      // Only show error if we know a gas wallet should exist
      if (hasGasWalletId === true) {
        message.error(error.response?.data?.message || 'Failed to fetch gas wallet');
      }
      console.error('Error fetching gas wallet:', error);
    } finally {
      setLoadingGasWallet(false);
    }
  };

  const initializeData = async () => {
    setLoading(true);

    // First load company data (synchronous)
    const hasGasWallet = await fetchCompanyData();

    // If company has gas wallet, load gas wallet data asynchronously
    if (hasGasWallet) {
      fetchGasWallet();
    }

    setLoading(false);
  };

  const refreshBalances = async () => {
    if (!companyId) return;

    try {
      setLoadingBalances(true);
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_GAS_WALLET.replace('{companyId}', companyId)
      );

      if (response.data?.status === 'success' && response.data?.data) {
        setGasBalances(response.data.data.gasBalances);
        message.success('Gas balances refreshed');
      }
    } catch (error: any) {
      message.error('Failed to refresh gas balances');
      console.error('Error refreshing gas balances:', error);
    } finally {
      setLoadingBalances(false);
    }
  };

  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  // Show loading state while fetching
  if (loading || hasGasWalletId === null) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  // Show "Pending Gas Wallet" only if we've confirmed there's no gasWalletId
  if (!gasWallet && hasGasWalletId === false) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/kivu/companies/${companyId}`)}>
            Back to Company
          </Button>
          <Typography.Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Gas Station
          </Typography.Title>
        </HeaderContainer>
        <Alert
          message="Pending Gas Wallet"
          description="This merchant company does not have a gas wallet assigned yet. Gas wallets are assigned when a merchant is activated by an administrator."
          type="warning"
          showIcon
          style={{ background: COLORS.bgSecondary, borderColor: COLORS.border }}
        />
      </PageContainer>
    );
  }

  // If gasWalletId exists but we couldn't fetch details, show error
  // Show loading state while fetching initial company data
  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  // Show company header even if gas wallet fails to load
  const renderCompanyHeader = (showRefreshButton = false) => (
    <HeaderContainer>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/kivu/companies/${companyId}`)}>
        Back to Company
      </Button>
      <Space>
        {companyData?.logoUrl && (
          <img
            src={companyData.logoUrl}
            alt="Company Logo"
            style={{ width: 40, height: 40, borderRadius: '8px', objectFit: 'cover' }}
          />
        )}
        <div>
          <Typography.Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Gas Station
          </Typography.Title>
          {companyData?.name && (
            <Typography.Text style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
              {companyData.name}
            </Typography.Text>
          )}
        </div>
        {showRefreshButton && (
          <Button
            icon={<ReloadOutlined />}
            onClick={refreshBalances}
            loading={loadingBalances}
          >
            Refresh Balances
          </Button>
        )}
      </Space>
    </HeaderContainer>
  );

  // Show error state when gas wallet failed to load (not loading anymore) but company data is available
  if (!gasWallet && !loadingGasWallet && hasGasWalletId === true) {
    return (
      <PageContainer>
        {renderCompanyHeader()}
        <Alert
          message="Error Loading Gas Wallet"
          description="A gas wallet is assigned to this merchant, but we couldn't load its details. Please try refreshing the page or contact support if the issue persists."
          type="error"
          showIcon
          style={{ background: COLORS.bgSecondary, borderColor: COLORS.border }}
        />
      </PageContainer>
    );
  }

  // Show loading state when gas wallet data is being fetched
  if (loadingGasWallet && hasGasWalletId === true) {
    return (
      <PageContainer>
        {renderCompanyHeader()}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spin size="large" tip="Loading gas wallet details..." />
        </div>
      </PageContainer>
    );
  }

  // Show empty state when no gas wallet is assigned
  if (hasGasWalletId === false) {
    return (
      <PageContainer>
        {renderCompanyHeader()}
        <Empty
          description="No gas wallet assigned to this company"
          style={{ color: COLORS.textSecondary }}
        />
      </PageContainer>
    );
  }

  const lowGasBalances = [
    ...(gasBalances?.evmGasBalances?.filter((balance: any) => balance.isLowGas) || []),
    ...(gasBalances?.solanaGasBalance && gasBalances.solanaGasBalance.isLowGas ? [gasBalances.solanaGasBalance] : []),
    ...(gasBalances?.tronGasBalance && gasBalances.tronGasBalance.isLowGas ? [{ ...gasBalances.tronGasBalance, currencySymbol: 'TRX', blockchainNetworkName: 'TRON' }] : []),
  ];

  return (
    <PageContainer>
      {renderCompanyHeader(true)}

      {lowGasBalances.length > 0 && (
        <Alert
          message="Low Gas Warning"
          description={
            <div style={{ color: COLORS.textSecondary }}>
              <p style={{ color: COLORS.textSecondary, marginBottom: 8 }}>The following gas balances are below $1 USD and may need to be topped up:</p>
              <ul style={{ marginTop: 8, marginBottom: 0, color: COLORS.textSecondary }}>
                {lowGasBalances.map((balance: any, index: number) => (
                  <li key={index} style={{ color: COLORS.textSecondary }}>
                    {balance.currencySymbol} on {balance.blockchainNetworkName}: ${Number(balance.balanceUsd || 0).toFixed(2)}
                  </li>
                ))}
              </ul>
            </div>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ 
            marginBottom: 24,
            background: `${COLORS.warning}10`,
            borderColor: COLORS.warning,
          }}
        />
      )}

      <StyledCard
        title={
          <Space>
            <WalletOutlined />
            <span>Gas Wallet Details</span>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="Wallet ID">
            <Space>
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {gasWallet.walletId || gasWallet.id}
              </span>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopy(gasWallet.walletId || gasWallet.id, 'Wallet ID')}
                style={{ color: COLORS.accent }}
              />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Member ID">{gasWallet.externalMemberId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Member Name">{gasWallet.externalMemberName || '-'}</Descriptions.Item>
          <Descriptions.Item label="Member Email">{gasWallet.externalMemberEmail || '-'}</Descriptions.Item>
          {gasWallet.evmAddress && (
            <Descriptions.Item label="EVM Address">
              <Space>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {gasWallet.evmAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(gasWallet.evmAddress, 'EVM Address')}
                  style={{ color: COLORS.accent }}
                />
              </Space>
            </Descriptions.Item>
          )}
          {gasWallet.solanaAddress && (
            <Descriptions.Item label="Solana Address">
              <Space>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {gasWallet.solanaAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(gasWallet.solanaAddress, 'Solana Address')}
                  style={{ color: COLORS.accent }}
                />
              </Space>
            </Descriptions.Item>
          )}
          {gasWallet.tronAddress && (
            <Descriptions.Item label="TRON Address">
              <Space>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {gasWallet.tronAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(gasWallet.tronAddress, 'TRON Address')}
                  style={{ color: COLORS.accent }}
                />
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </StyledCard>

      <StyledCard
        title={
          <Space>
            <DollarOutlined />
            <span>Gas Balances</span>
            {gasBalances?.totalUsd !== undefined && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                Total: ${Number(gasBalances.totalUsd).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Tag>
            )}
          </Space>
        }
      >
        {gasBalances?.evmGasBalances && gasBalances.evmGasBalances.length > 0 ? (
          // Group balances by blockchain network
          Object.values(
            gasBalances.evmGasBalances.reduce((acc: any, balance: any) => {
              const networkId = balance.blockchainNetworkId;
              if (!acc[networkId]) {
                acc[networkId] = {
                  chainId: networkId,
                  chainName: balance.blockchainNetworkName,
                  chainIconUrl: balance.blockchainNetworkIconUrl,
                  networkId: balance.networkId,
                  balances: [],
                  totalUsd: 0,
                };
              }
              acc[networkId].balances.push(balance);
              acc[networkId].totalUsd += balance.balanceUsd || 0;
              return acc;
            }, {})
          ).map((chain: any) => (
            <ChainCard
              key={chain.chainId}
              title={
                <Space>
                  {chain.chainIconUrl && (
                    <ChainIcon src={chain.chainIconUrl} alt={chain.chainName} />
                  )}
                  <span>{chain.chainName}</span>
                  {chain.totalUsd !== undefined && (
                    <Tag color="blue">
                      ${Number(chain.totalUsd).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Tag>
                  )}
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              {chain.balances && chain.balances.length > 0 ? (
                chain.balances.map((balance: any, index: number) => (
                  <BalanceRow key={index}>
                    <BalanceInfo>
                      {balance.currencyIconUrl && (
                        <CurrencyIcon src={balance.currencyIconUrl} alt={balance.currencySymbol} />
                      )}
                      {balance.blockchainNetworkIconUrl && !balance.currencyIconUrl && (
                        <CurrencyIcon src={balance.blockchainNetworkIconUrl} alt={balance.blockchainNetworkName} />
                      )}
                      <div>
                        <div style={{ fontWeight: 500, color: COLORS.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
                          {balance.currencyName || balance.currencySymbol}
                          {balance.isLowGas && (
                            <Tag color="orange" icon={<WarningOutlined />}>
                              Low Gas
                            </Tag>
                          )}
                        </div>
                        {balance.error && (
                          <div style={{ marginTop: 4 }}>
                            <Tag color="red" icon={<WarningOutlined />}>
                              {balance.error}
                            </Tag>
                          </div>
                        )}
                      </div>
                    </BalanceInfo>
                    <BalanceAmounts>
                      <div style={{ fontWeight: 600, fontSize: '16px', color: COLORS.textPrimary }}>
                        {Number(balance.balance || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 8,
                        })}{' '}
                        {balance.currencySymbol}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                        ${Number(balance.balanceUsd || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </BalanceAmounts>
                  </BalanceRow>
                ))
              ) : (
                <Empty
                  description="No balances found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ color: COLORS.textSecondary }}
                />
              )}
            </ChainCard>
          ))

        ) : (
          <Empty
            description="No gas balances available"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ color: COLORS.textSecondary }}
          />
        )}

        {/* Solana Gas Balance */}
        {gasBalances?.solanaGasBalance && (
          <ChainCard
            title={
              <Space>
                <span>Solana</span>
                <Tag color="blue">
                  ${Number(gasBalances.solanaGasBalance.balanceUsd || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Tag>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <BalanceRow>
              <BalanceInfo>
                <div>
                  <div style={{ fontWeight: 500, color: COLORS.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
                    SOL
                    {gasBalances.solanaGasBalance.isLowGas && (
                      <Tag color="orange" icon={<WarningOutlined />}>
                        Low Gas
                      </Tag>
                    )}
                  </div>
                  {gasBalances.solanaGasBalance.error && (
                    <div style={{ marginTop: 4 }}>
                      <Tag color="red" icon={<WarningOutlined />}>
                        {gasBalances.solanaGasBalance.error}
                      </Tag>
                    </div>
                  )}
                </div>
              </BalanceInfo>
              <BalanceAmounts>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: COLORS.textPrimary }}>
                  {Number(gasBalances.solanaGasBalance.balance || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 8,
                  })}{' '}
                  SOL
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                  ${Number(gasBalances.solanaGasBalance.balanceUsd || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </BalanceAmounts>
            </BalanceRow>
          </ChainCard>
        )}

        {/* TRON Gas Balance (TRX for bandwidth/energy) */}
        {gasBalances?.tronGasBalance && (
          <ChainCard
            title={
              <Space>
                <span>TRON</span>
                <Tag color="red">
                  ${Number(gasBalances.tronGasBalance.balanceUsd || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Tag>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <BalanceRow>
              <BalanceInfo>
                <div>
                  <div style={{ fontWeight: 500, color: COLORS.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
                    TRX
                    {gasBalances.tronGasBalance.isLowGas && (
                      <Tag color="orange" icon={<WarningOutlined />}>
                        Low Gas
                      </Tag>
                    )}
                  </div>
                  {gasBalances.tronGasBalance.error && (
                    <div style={{ marginTop: 4 }}>
                      <Tag color="red" icon={<WarningOutlined />}>
                        {gasBalances.tronGasBalance.error}
                      </Tag>
                    </div>
                  )}
                </div>
              </BalanceInfo>
              <BalanceAmounts>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: COLORS.textPrimary }}>
                  {Number(gasBalances.tronGasBalance.balance || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 8,
                  })}{' '}
                  TRX
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                  ${Number(gasBalances.tronGasBalance.balanceUsd || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </BalanceAmounts>
            </BalanceRow>
          </ChainCard>
        )}
      </StyledCard>

      <Alert
        message="About Gas Wallets"
        description={
          <div style={{ color: COLORS.textSecondary }}>
            <p style={{ color: COLORS.textSecondary, marginBottom: 8 }}>The gas wallet is used to fund child wallets with gas (native tokens) for EVM and Solana sweeps.</p>
            <p style={{ marginTop: 8, marginBottom: 0, color: COLORS.textSecondary }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              Low gas balances (less than $1 USD) are flagged with a warning. Ensure sufficient gas is available for sweep operations.
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ background: COLORS.bgSecondary, borderColor: COLORS.border }}
      />
    </PageContainer>
  );
};

export default GasStation;

